import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Users,
  ChevronRight,
  Plus
} from 'lucide-react';
import { HEALTH_GREEN } from '../../../Login/utils/mockData';
import agthealthService from '../../../../services/agthealthService';
import PatientModal from '../../patients/PatientModal';

const WidgetCard = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
}));

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
  overflow: 'auto',
}));

const CardFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
  display: 'flex',
  justifyContent: 'center',
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(0.5, 2),
  textTransform: 'none',
  color: HEALTH_GREEN,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
  }
}));

const AddIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  width: 32,
  height: 32
}));

const PatientWidget = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Estado para el modal
  const [patientModal, setPatientModal] = useState({
    open: false,
    patientId: null
  });
  
  // Función para cargar los pacientes
  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await agthealthService.getPatients();
      // Mostrar solo los 5 primeros pacientes en el widget
      setPatients((response.data || []).slice(0, 5));
      setError(null);
    } catch (err) {
      console.error('Error al cargar pacientes:', err);
      setError('No se pudieron cargar los pacientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleViewPatient = (patientId) => {
    navigate(`/patients/${patientId}`);
  };

  const handleViewAll = () => {
    navigate('/patients');
  };

  const handleAddPatient = () => {
    setPatientModal({ open: true, patientId: null });
  };
  
  const handleClosePatientModal = () => {
    setPatientModal({ ...patientModal, open: false });
  };
  
  const handlePatientSaved = () => {
    // Recargar la lista de pacientes después de guardar
    fetchPatients();
  };

  // Función para generar iniciales del nombre
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  };

  return (
    <>
      <WidgetCard>
        <CardHeader>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Users size={18} style={{ marginRight: 8 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Mis Pacientes
            </Typography>
          </Box>
          <AddIconButton onClick={handleAddPatient} size="small">
            <Plus size={16} />
          </AddIconButton>
        </CardHeader>

        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={30} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>
          ) : patients.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No tienes pacientes registrados aún
              </Typography>
              <IconButton
                color="primary"
                onClick={handleAddPatient}
                sx={{ mt: 2, backgroundColor: HEALTH_GREEN, color: 'white' }}
              >
                <Plus size={16} />
              </IconButton>
            </Box>
          ) : (
            <List disablePadding>
              {patients.map((patient, index) => (
                <React.Fragment key={patient.patientId}>
                  <ListItem 
                    button
                    onClick={() => handleViewPatient(patient.patientId)}
                    sx={{ px: 1, borderRadius: 2 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: HEALTH_GREEN }}>
                        {getInitials(patient.firstName, patient.lastName)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${patient.firstName} ${patient.lastName}`}
                      secondary={`ID: ${patient.identificationNumber}`}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <ChevronRight size={16} />
                  </ListItem>
                  {index < patients.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>

        {patients.length > 0 && (
          <CardFooter>
            <ViewAllButton 
              endIcon={<ChevronRight size={16} />}
              onClick={handleViewAll}
            >
              Ver Todos los Pacientes
            </ViewAllButton>
          </CardFooter>
        )}
      </WidgetCard>
      
      {/* Modal de paciente */}
      <PatientModal
        open={patientModal.open}
        onClose={handleClosePatientModal}
        patientId={patientModal.patientId}
        onSaved={handlePatientSaved}
      />
    </>
  );
};

export default PatientWidget;